import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Layers, Clock, TrendingUp } from "lucide-react";
import SEO from "@/components/SEO";
import CTABand from "@/components/CTABand";
import { Container, Reveal, MaskText } from "@/components/Reveal";
import ServiceIcon from "@/components/ServiceIcon";
import EduSearch from "@/components/education/EduSearch";
import { ArticleCard } from "@/components/education/ArticleCard";
import {
  CATEGORIES,
  LEARNING_PATHS,
  GLOSSARY,
  ARTICLES,
  featuredArticles,
  recentArticles,
  popularArticles,
} from "@/data/education";

export default function EducationCenter() {
  const featured = featuredArticles(3);
  const recent = recentArticles(6);
  const popular = popularArticles(6);
  const glossaryPreview = GLOSSARY.slice(0, 12);

  const stats = [
    { v: `${ARTICLES.length}+`, l: "In-depth articles" },
    { v: `${CATEGORIES.length}`, l: "Categories" },
    { v: `${LEARNING_PATHS.length}`, l: "Learning paths" },
    { v: `${GLOSSARY.length}+`, l: "Glossary terms" },
  ];

  return (
    <>
      <SEO
        title="Education Center"
        description="A structured commercial energy knowledge library from Innervation Energy Group — articles, learning paths, and a searchable glossary for business owners, CFOs, and procurement professionals."
        path="/education"
      />

      {/* Hero with search */}
      <section className="relative overflow-hidden bg-navy pt-[74px] text-white md:pt-[88px]">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-50" />
        <div className="pointer-events-none absolute -right-32 top-10 h-[400px] w-[400px] rounded-full bg-electric/15 blur-[140px]" />
        <Container className="relative py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-energy">
              Education Center
            </p>
            <h1 className="mt-5 font-heading text-4xl font-semibold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              <MaskText lines={["The commercial energy", "knowledge library."]} />
            </h1>
            <Reveal delay={0.35}>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
                We believe informed businesses make better energy decisions. This is a
                structured library built to educate first — for business owners, property
                owners, CFOs, controllers, procurement teams, and facility managers.
              </p>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="mt-9 max-w-2xl">
                <EduSearch />
              </div>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-10 md:grid-cols-4">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div>
                  <div className="font-heading text-3xl font-semibold md:text-4xl">{s.v}</div>
                  <p className="mt-2 text-sm text-white/55">{s.l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Featured */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="flex items-end justify-between">
            <div>
              <p className="overline text-electric">Featured Articles</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                Start here.
              </h2>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {featured.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.07}>
                <ArticleCard article={a} testid={`featured-article-${a.slug}`} />
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Browse by category */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Browse by Category</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Explore the library by topic.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={c.external || `/education/category/${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group flex h-full flex-col border border-line bg-white p-8 transition-colors hover:border-electric"
                >
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-sm"
                    style={{ backgroundColor: `${c.color}14`, color: c.color }}
                  >
                    <ServiceIcon name={c.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-medium text-navy transition-colors group-hover:text-electric">
                    {c.label}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slatey">
                    {c.tagline}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-electric">
                    Explore
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Learning paths */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="flex items-center gap-3">
            <Layers className="h-5 w-5 text-electric" />
            <p className="overline text-electric">Learning Paths</p>
          </div>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Guided journeys from beginner to advanced.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {LEARNING_PATHS.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/education/paths/${p.slug}`}
                  data-testid={`path-card-${p.slug}`}
                  className="group flex h-full flex-col border border-line p-8 transition-colors hover:border-electric"
                >
                  <div className="flex items-center justify-between">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-sm"
                      style={{ backgroundColor: `${p.color}14`, color: p.color }}
                    >
                      <ServiceIcon name={p.icon} className="h-6 w-6" />
                    </div>
                    <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slatey">
                      {p.articles.length} articles
                    </span>
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-medium text-navy transition-colors group-hover:text-electric">
                    {p.title}
                  </h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.12em] text-energy">
                    {p.level}
                  </p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slatey">{p.summary}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-electric">
                    Start path
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Recent + Popular */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-electric" />
                <p className="overline text-electric">Recently Added</p>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {recent.slice(0, 4).map((a, i) => (
                  <Reveal key={a.slug} delay={(i % 2) * 0.06}>
                    <ArticleCard article={a} testid={`recent-article-${a.slug}`} />
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-electric" />
                <p className="overline text-electric">Popular Articles</p>
              </div>
              <div className="mt-8 divide-y divide-line border-t border-line">
                {popular.map((a, i) => (
                  <Reveal key={a.slug} delay={i * 0.05}>
                    <Link
                      to={`/education/${a.slug}`}
                      data-testid={`popular-article-${a.slug}`}
                      className="group flex items-start gap-5 py-5"
                    >
                      <span className="font-mono text-lg text-slatey">0{i + 1}</span>
                      <div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-electric">
                          {a.categoryLabel}
                        </span>
                        <h4 className="mt-1 font-heading text-base font-medium leading-snug text-navy transition-colors group-hover:text-electric">
                          {a.title}
                        </h4>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Glossary preview */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-electric" />
                <p className="overline text-electric">Commercial Energy Glossary</p>
              </div>
              <h2 className="mt-4 max-w-2xl font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                Speak the language of commercial energy.
              </h2>
            </div>
            <Link
              to="/education/glossary"
              className="group inline-flex items-center gap-2 text-sm font-medium text-navy"
            >
              View full glossary
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {glossaryPreview.map((g) => (
              <Link
                key={g.slug}
                to={`/education/glossary/${g.slug}`}
                data-testid={`glossary-chip-${g.slug}`}
                className="border border-line px-5 py-3 text-sm font-medium text-navy transition-colors hover:border-electric hover:text-electric"
              >
                {g.term}
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <CTABand
        heading="Have a commercial energy question?"
        text="Our advisors are happy to point you to the right resource — or walk you through your specific situation directly."
        secondary={{ label: "Browse Resources", to: "/resources" }}
      />
    </>
  );
}
